import { answerLines, type Homework, type HomeworkAnswer } from '@adhd/shared';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeftIcon } from '../components/Icons.js';
import { formatUntil } from '../components/KeepInMind.js';
import { api } from '../lib/api.js';
import { useSession } from '../lib/session.js';

/**
 * A homework, on paper, for the room it was set in.
 *
 * THIS IS THE HALF THAT WAS MISSING. The app was collecting a fortnight of
 * writing and doing nothing with it — his words, "are we just randomly
 * collecting things and that's it?" Homework exists to be taken back to the
 * person who set it, and the app already knew how to do that for thought
 * records: a sheet, dated, printed from the same markup you read on screen.
 * This is that, for homework.
 *
 * NO MODEL. It prints what he wrote, in the order he wrote it, under the
 * question he was asked. It works at the `off` rung and on a server with no AI
 * configured, which matters because handing something over is the whole point
 * and must not depend on a provider being up.
 *
 * NO COUNT AGAINST THE SPAN. The days he wrote are listed; the days he did not
 * are simply absent. A fraction on a sheet a therapist reads is a grade, and
 * the one thing this app will not do is hand somebody a mark out of fourteen.
 */
export function HomeworkSheet(): JSX.Element {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const { user } = useSession();
  const [hw, setHw] = useState<Homework | null>(null);
  const [answers, setAnswers] = useState<HomeworkAnswer[]>([]);

  useEffect(() => {
    api
      .homeworkOne(id)
      .then((r) => {
        setHw(r.homework);
        // Oldest first: on paper a fortnight reads forwards, unlike the screen
        // where the newest thing is the one you just wrote.
        setAnswers([...r.answers].reverse());
      })
      .catch(() => {});
  }, [id]);

  if (!hw) return <div className="screen stack" />;

  return (
    <div className="screen stack">
      <button className="linkish back-link print-action" onClick={() => navigate(`/homework/${id}`)}>
        <ArrowLeftIcon /> Back
      </button>

      <h1>
        {hw.title}
        {user ? ` — ${user.displayName}` : ''}
      </h1>
      <p className="sheet-meta">
        <span>
          {formatUntil(hw.startedOn)} to {formatUntil(hw.endsOn)}
        </span>
        <span>
          {answers.length === 0
            ? 'Nothing written'
            : `Written on ${hw.answeredDays} ${hw.answeredDays === 1 ? 'day' : 'days'}`}
        </span>
        <span>Printed {formatUntil(new Date().toISOString().slice(0, 10))}</span>
      </p>

      {answers.map((a) => (
        <article key={a.id} className="card stack-sm stack">
          <span className="faint">{formatUntil(a.day)}</span>
          {answerLines(hw.fields, a.values).map((line, i) => (
            <div key={i}>
              {line.label !== null && hw.fields.length > 1 && (
                <span className="faint">{line.label}</span>
              )}
              <p className="reading" style={{ margin: 0, whiteSpace: 'pre-wrap' }}>
                {line.text}
              </p>
            </div>
          ))}
        </article>
      ))}

      <button className="btn btn-primary btn-block print-action" onClick={() => window.print()}>
        Print or save
      </button>
    </div>
  );
}

